<template>
  <v-container fluid class="templates-container pa-0">
    <!-- Modern Header -->
    <div v-if="showHeader" class="header-section mb-8">
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-text-box-outline</v-icon>
        </div>
        <div class="flex-grow-1">
          <h1 class="page-title mb-1">Message Templates</h1>
          <p class="page-subtitle mb-0">
            Create and manage reusable message templates
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex mr-3">
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-flash</v-icon>
            Quick Reply
          </v-chip>
          <v-chip
            class="white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-content-save</v-icon>
            Reusable
          </v-chip>
        </div>
        <v-btn
          color="white"
          depressed
          large
          style="border-radius: 8px;"
          :to="{ path: `/templates/add` }"
        >
          <v-icon left color="primary">mdi-plus-circle</v-icon>
          <span class="primary--text font-weight-medium">Add Template</span>
        </v-btn>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0">
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-message-text</v-icon
            >
            <span>Text Templates</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2">mdi-flash</v-icon>
            <span>Quick Replies</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-content-save</v-icon
            >
            <span>Save & Reuse</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-format-text</v-icon
            >
            <span>Formatting</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <div class="mx-3 mx-md-5 mt-4">
      <v-card class="modern-card customBorderRadius" elevation="0">
        <div class="card-header-green"></div>
        <v-card-title class="pt-6 pb-4">
          <v-icon left color="primary">mdi-format-list-bulleted</v-icon>
          <span class="text-h6 font-weight-medium">Your Templates</span>
        </v-card-title>

        <v-card-text class="pa-6">
          <div
            v-if="preloader"
            style="text-align: center; padding: 25px"
            align="center"
          >
            <v-row class="fill-height" align-content="center" justify="center">
              <v-col class="subtitle-1 text-center" cols="12">
                Please Wait
              </v-col>
              <v-col cols="6">
                <v-progress-linear
                  color="primary"
                  indeterminate
                  rounded
                  height="6"
                ></v-progress-linear>
              </v-col>
            </v-row>
          </div>
          <div v-else-if="templates.length > 0">
            <v-list two-line class="transparent">
              <template v-for="(item, i) in templates">
                <v-list-item
                  :key="i"
                  :to="{ path: `/templates/${item.id}` }"
                  class="template-list-item mb-3"
                >
                  <v-list-item-avatar color="primary" class="white--text">
                    <v-icon color="white">mdi-text</v-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title
                      class="font-weight-medium"
                      v-text="item.title"
                    ></v-list-item-title>
                    <v-list-item-subtitle
                      v-text="item.desc"
                    ></v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action v-if="item.isLink || item.type == 'link'">
                    <v-chip color="primary" small outlined>
                      <v-icon left small>mdi-link</v-icon>
                      Link
                    </v-chip>
                  </v-list-item-action>

                  <v-list-item-action>
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-list-item-action>
                </v-list-item>
                <v-divider
                  v-if="i < templates.length - 1"
                  :key="'divider-' + i"
                ></v-divider>
              </template>
            </v-list>
          </div>
          <div v-else class="text-center pa-8">
            <v-icon size="80" color="grey lighten-2"
              >mdi-text-box-remove-outline</v-icon
            >
            <p class="text-h6 mt-4 grey--text">No templates yet</p>
            <p class="text-caption grey--text">
              Create your first template to get started
            </p>
            <v-btn
              color="primary"
              depressed
              large
              class="mt-4"
              style="border-radius: 8px;"
              :to="{ path: `/templates/add` }"
            >
              <v-icon left>mdi-plus-circle</v-icon>
              Add Template
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Floating Add Template FAB -->
    <v-btn
      class="fab-add-template"
      color="primary"
      fab
      dark
      large
      @click="$router.push({ path: '/templates/add' })"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>
  </v-container>
</template>

<script>
const _ = require("lodash");

export default {
  data() {
    return {
      showHeader: false,
      templates: [],
      allTemplates: [],
      preloader: false,
    };
  },
  computed: {},
  created() {
    this.showHeader =
      localStorage.getItem("templatesHeaderDismissed") !== "true";
    this.getTemplates();
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("templatesHeaderDismissed", "true");
    },
    filterByLink(active) {
      if (active) {
        this.templates = _.filter(this.allTemplates, (t) => {
          return t.isLink;
        });
        return;
      }

      this.templates = this.allTemplates;
    },
    getTemplates() {
      this.preloader = true;
      this.templates = [];
      this.$http
        .get(
          this.$store.state.ENDPOINT +
            "/templates?filter[where][companyId]=" +
            this.$store.getters.user.companyId +
            "&filter[where][templateType][neq]=whatsapp",
        )
        .then(
          (response) => {
            response = response.body;
            this.allTemplates = response;
            this.templates = this.allTemplates;
            this.preloader = false;
          },
          (error) => {
            this.preloader = false;
            this.$swal({
              type: "error",
              text: "Something went wrong while loading the templates.",
            });
          },
        );
    },
  },
};
</script>

<style scoped>
.templates-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .templates-container {
  background-color: #121212 !important;
}

.header-section {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px;
  padding: 32px;
  color: white;
  box-shadow: 0 4px 20px rgba(55, 207, 55, 0.3);
}

.header-icon-wrapper {
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: white;
  margin: 0;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.feature-pills {
  margin-top: 24px;
}

.feature-pill {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.feature-pill:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.modern-card {
  background: var(--v-background-base, white);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.customBorderRadius {
  border-radius: 12px !important;
  overflow: hidden !important;
}

.card-header-green {
  height: 4px;
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  border-radius: 12px 12px 0 0;
}

.template-list-item {
  background: var(--v-background-base, #f8f9fa);
  border-radius: 8px;
  padding: 12px 16px !important;
  transition: all 0.2s;
}

.theme--dark .template-list-item {
  background: #2d2d2d !important;
}

.template-list-item:hover {
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.theme--dark .template-list-item:hover {
  background: #333333 !important;
}

.fab-add-template {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 30;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

@media (max-width: 960px) {
  .header-icon-wrapper {
    width: 56px;
    height: 56px;
  }

  .page-title {
    font-size: 28px;
  }
}
</style>

<style>
/* Make dividers transparent only in templates page */
.templates-container .v-divider {
  border-color: transparent !important;
}
</style>
